<script setup lang="ts">
	const props = defineProps<{
		amount?: number | null;
		paymentStatusBgColor: string;
		paymentStatusName: string;
		isPaid?: boolean;
	}>();

	const hasAmount = computed(
		() => typeof props.amount === 'number' && Number.isFinite(props.amount) && props.amount > 0
	);

	const formattedAmount = computed(() => {
		if (!hasAmount.value) return '';

		return new Intl.NumberFormat('ru-RU', {
			maximumFractionDigits: 0
		}).format(props.amount!);
	});

	const statusIcon = computed(() => {
		if (typeof props.isPaid === 'boolean') {
			return props?.isPaid ? 'material-symbols:verified-rounded' : 'ix:certificate-exclamation-filled';
		} else {
			return hasAmount.value ? 'material-symbols:verified-rounded' : 'ix:certificate-exclamation-filled';
		}
	});

	const statusStyles = computed(() => ({
		'--payment-color': props.paymentStatusBgColor
	}));
</script>

<template>
	<div
		class="cargo-payment-status"
		:class="{ 'cargo-payment-status--paid': !hasAmount }"
		:style="statusStyles"
	>
		<div class="cargo-payment-status__content">
			<span class="cargo-payment-status__label">
				{{ hasAmount ? 'Сумма к оплате' : 'Статус оплаты' }}
			</span>

			<div
				v-if="hasAmount"
				class="cargo-payment-status__amount"
			>
				{{ formattedAmount }} ₸
			</div>
		</div>

		<div class="cargo-payment-status__badge">
			<Icon
				:name="statusIcon"
				:color="paymentStatusBgColor"
				size="26px"
			/>

			<span class="cargo-payment-status__divider" />

			<span class="cargo-payment-status__name">
				{{ paymentStatusName }}
			</span>
		</div>
	</div>
</template>

<style scoped>
	.cargo-payment-status {
		--payment-color: #fc0000;

		display: flex;
		align-items: center;
		justify-content: space-between;
		flex-wrap: wrap;
		gap: 16px;

		min-height: 60px;
		padding: 12px 16px;
		border-radius: 16px;

		background: color-mix(in srgb, var(--payment-color) 7%, white);
		border: 1px solid color-mix(in srgb, var(--payment-color) 50%, white);
	}

	.cargo-payment-status__content {
		display: flex;
		flex-direction: column;
		gap: 6px;
		min-width: 0;
	}

	.cargo-payment-status__label {
		font-size: 14px;
		font-weight: 500;
	}

	.cargo-payment-status__amount {
		font-size: 26px;
		line-height: 1;
		font-weight: 700;
		color: var(--payment-color);
		white-space: nowrap;
	}

	.cargo-payment-status__badge {
		display: inline-flex;
		align-items: center;
		gap: 12px;

		padding: 12px 14px;
		border-radius: 12px;

		background: color-mix(in srgb, var(--payment-color) 20%, white);

		color: var(--payment-color);
	}

	.cargo-payment-status__divider {
		width: 1px;
		height: 24px;
		flex-shrink: 0;
		background: color-mix(in srgb, var(--payment-color) 30%, transparent);
	}

	.cargo-payment-status__name {
		font-size: 15px;
		font-weight: 600;
		white-space: nowrap;
	}

	.cargo-payment-status--paid {
		min-height: 84px;
	}

	@media (max-width: 380px) {
		.cargo-payment-status {
			padding: 14px;
			gap: 12px;
		}

		.cargo-payment-status__amount {
			font-size: 26px;
		}

		.cargo-payment-status__badge {
			padding: 10px;
			gap: 8px;
		}
	}
</style>
